import type { BaseModel } from "../shared"
import type { Preset } from "../presets"
import type { Station } from "../stations"

// Modulation types (used in presets but defined in admin as configuration)
export interface ModulationExtraParams {
  lora_type_bw: Record<string, number[]>
  sf_lora: number[]
  chirp_type: string[]
}

export interface Modulation {
  name: string
  extra_params: ModulationExtraParams | null
}

// Admin/Configuration types
export type ResponsibilityZoneCoordinates = [number, number][]

export interface ZoneOutput {
  id?: string
  name: string
  responsibility_zone?: ResponsibilityZoneCoordinates | null
  zone_color?: string | null
}

export interface OrgZonesOutput extends ZoneOutput {
  user_groups: ZoneOutput[]
}

export interface Organization extends BaseModel {
  name: string
  users?: User[]
  responsibility_zone?: ResponsibilityZoneCoordinates | null
  zone_color?: string | null
}

export interface User extends BaseModel {
  username: string
  profile_image: string | null
  show_bw_suggestions: boolean
  show_alerts: boolean
  role: number | RoleName
  org: {
    name: string
    id: string
  }
  org_id: string
  selected?: boolean
  access_objects?: string[]
}

export interface UserCreate {
  username: string
  password: string
  role: number | RoleName
  org_id: string
}

export interface UserGroup extends BaseModel {
  name: string
  org_id: string
  users: User[]
  stations: Station[]
  responsibility_zone?: ResponsibilityZoneCoordinates | null
  zone_color?: string | null
}

export interface UserGroupPayload {
  name?: string
  user_ids?: string[]
  station_ids?: string[]
  org_id?: string
  responsibility_zone?: ResponsibilityZoneCoordinates | null
  zone_color?: string | null
}

// Role mapping for numeric roles
export type RoleName = "viewer" | "user" | "engineer" | "admin" | "developer"
export type RoleValue = 0 | 1 | 2 | 3 | 4

export const ROLE_MAPPING = {
  0: "viewer",
  1: "user",
  2: "engineer",
  3: "admin",
  4: "developer",
} as const

export const ROLE_OPTIONS: { value: RoleValue; label: RoleName }[] = [
  { value: 0, label: "viewer" },
  { value: 1, label: "user" },
  { value: 2, label: "engineer" },
  { value: 3, label: "admin" },
  { value: 4, label: "developer" },
]

export const ROLE_NAME_TO_VALUE: Record<RoleName, RoleValue> = {
  viewer: 0,
  user: 1,
  engineer: 2,
  admin: 3,
  developer: 4,
}

export const normalizeRoleValue = (role: number | string): RoleValue => {
  if (typeof role === "number") {
    return role as RoleValue
  }

  const numericRole = Number(role)
  if (!Number.isNaN(numericRole)) {
    return numericRole as RoleValue
  }

  return ROLE_NAME_TO_VALUE[role as RoleName] ?? ROLE_NAME_TO_VALUE.user
}

export const getRoleLabel = (role: number | string): RoleName =>
  ROLE_MAPPING[normalizeRoleValue(role)]

// Configuration types
export interface AmpfConfig extends BaseModel {
  name: string
  max_freq: number
  min_freq: number

  // Antenna related fields
  distance: number
  coverage_angle: number
  with_rotation: boolean
}

export interface RelayConfig extends BaseModel {
  min_freq: number
  max_freq: number
}

export interface AmplifierConfig extends RelayConfig {
  name: string
  index: number
  pin: number
  second_index?: number | null
  servo_pin: number | null
  mode: number | string
  with_rotation: boolean
  distance: number
  coverage_angle: number
  order: number
  default_enable?: boolean
  display_name?: string | null
  servo_offset: number | null
  power_factor: number
}

export interface Relay {
  id: string
  name: string
  isActive: boolean
}

// Amplifier types
export const antennaAngles = [0, 45, 90] as const
export type AntennaAngle = (typeof antennaAngles)[number]
export const FreqInputMode = ["DEFAULT", "CENTRAL"] as const
export interface Amplifier extends BaseModel {
  unit_id: string
  config: AmplifierConfig
  config_id: string
  modulation: string
  /** LoRa CSF (spreading factor); null for waveforms */
  sf?: number | null
  /** LoRa CS: up | down | alternating */
  chirp?: string | null
  /** LoRa CBW in kHz */
  cbw_khz?: number | null
  freq_input_mode: (typeof FreqInputMode)[number]
  start_frequency: number
  end_frequency: number
  bandwidth: number
  sweep_time: number
  sweeps: [number, number][]
  last_preset?: Preset
  timer: number
  custom_sweeps: boolean
  polarization_angle: AntennaAngle
}

// Access control
export interface AddOrRemoveAccess {
  subject_id: string
  object_id: string
  action: "add" | "remove"
}

export interface NotificationRecipient {
  id: string
  collection?: string
  username?: string
  role?: number
}

export interface NotificationItem {
  id: string
  type: string
  title: string
  message: string
  sender_id: string
  sender_username: string
  priority: "low" | "medium" | "high" | "critical" | string
  additional_data: unknown | null
  recipient: NotificationRecipient
  is_read: boolean
  redirect_url?: string
  created_at?: string
  updated_at?: string | null
  detail_key?: string
  username?: string
}
